import { SessionStatus, SessionEvent } from "@/mds/components/sessions/session.types";
import {
  MDSSessionState,
  MDSSessionAction,
  MDSSessionActionTypes,
} from "./mds-session-context.types";

type CreateInitialStateProps = Pick<MDSSessionState, "sessionEvents"> & {
  sessionStatus: SessionStatus;
};

export const createInitialState = ({
  sessionEvents,
  sessionStatus,
}: CreateInitialStateProps): MDSSessionState => ({
  activeArtifact: null,
  sessionEvents,
  sessionStatus,
  isMinimized: false,
  reasoningGroups: {},
  countdownDurationMs: 0,
  isCountdownActive: false,
  isCountdownPaused: false,
  isInputFocused: false,
  isHovering: false,
  isInNotificationState: false,
});

export const mdsSessionReducer = (
  state: MDSSessionState,
  action: MDSSessionActionTypes
): MDSSessionState => {
  switch (action.type) {
    case MDSSessionAction.SetActiveArtifact:
      return {
        ...state,
        activeArtifact: action.payload,
      };

    case MDSSessionAction.SetExtensionFocusState:
      return {
        ...state,
        activeArtifact: null,
      };

    case MDSSessionAction.AddSessionEvent: {
      const existingIndex = state.sessionEvents.findIndex(event => event.id === action.payload.id);

      if (existingIndex >= 0) {
        // Replace existing event (upsert behavior)
        const updatedEvents = [...state.sessionEvents];
        updatedEvents[existingIndex] = action.payload;
        return {
          ...state,
          sessionEvents: updatedEvents,
        };
      } else {
        // Insert new event
        return {
          ...state,
          sessionEvents: [...state.sessionEvents, action.payload],
        };
      }
    }

    case MDSSessionAction.UpdateSessionEvents: {
      // Build a map of existing events by ID for efficient lookup
      const existingEventMap = new Map<string, { event: SessionEvent; index: number }>();
      state.sessionEvents.forEach((event, index) => {
        existingEventMap.set(event.id, { event, index });
      });

      // Create a new array starting with existing events
      const updatedEvents = [...state.sessionEvents];

      // Process each new event
      action.payload.forEach(newEvent => {
        const existing = existingEventMap.get(newEvent.id);

        if (existing) {
          // Replace existing event at its current position
          updatedEvents[existing.index] = newEvent;
        } else {
          // Add new event to the end
          updatedEvents.push(newEvent);
        }
      });

      return {
        ...state,
        sessionEvents: updatedEvents,
      };
    }

    case MDSSessionAction.UpdateSessionStatus: {
      return {
        ...state,
        sessionStatus: action.payload,
      };
    }

    case MDSSessionAction.MinimizeSession: {
      return {
        ...state,
        isMinimized: true,
        isCountdownActive: false,
      };
    }

    case MDSSessionAction.ExpandSession: {
      return {
        ...state,
        isMinimized: false,
      };
    }

    case MDSSessionAction.ToggleReasoningGroup: {
      const { groupId } = action.payload;
      const current = state.reasoningGroups[groupId] ?? { isCollapsed: true, userToggled: false };
      return {
        ...state,
        reasoningGroups: {
          ...state.reasoningGroups,
          [groupId]: {
            isCollapsed: !current.isCollapsed,
            userToggled: true,
          },
        },
      };
    }

    case MDSSessionAction.AutoCollapseReasoningGroup: {
      const { groupId } = action.payload;
      const current = state.reasoningGroups[groupId] ?? { isCollapsed: true, userToggled: false };
      return {
        ...state,
        reasoningGroups: {
          ...state.reasoningGroups,
          [groupId]: {
            ...current,
            isCollapsed: true,
          },
        },
      };
    }

    case MDSSessionAction.StartCountdown: {
      return {
        ...state,
        countdownDurationMs: action.payload.durationMs,
        isCountdownActive: true,
        isCountdownPaused: false,
      };
    }

    case MDSSessionAction.StopCountdown: {
      return {
        ...state,
        isCountdownActive: false,
        isCountdownPaused: false,
      };
    }

    case MDSSessionAction.PauseCountdown: {
      return {
        ...state,
        isCountdownPaused: true,
      };
    }

    case MDSSessionAction.ResumeCountdown: {
      return {
        ...state,
        isCountdownPaused: false,
      };
    }

    case MDSSessionAction.SetNotificationState: {
      return {
        ...state,
        isInNotificationState: action.payload,
      };
    }

    case MDSSessionAction.SetInputFocused: {
      return {
        ...state,
        isInputFocused: action.payload,
      };
    }

    case MDSSessionAction.SetHovering: {
      return {
        ...state,
        isHovering: action.payload,
      };
    }

    default: {
      return state;
    }
  }
};
